import React, { FC, useContext } from "react";
import cx from "classnames";
import { Checkbox } from "@pepdirect/ui/checkbox";
import { Subscription_Options } from "constants/subscription";
import { convertFromCentsToDollars } from "helpers/currency";
import { Discount } from "services/graphql/generated";
import { logEvent } from "@pepdirect/helpers/analyticsLogger";
import { UserContext } from "context/user";
import { EVENT_NAMES } from "@pepdirect/shared/types";
import st from "./priceChooser.module.scss";

const { SUB_SAVE, ONE_TIME } = Subscription_Options;

interface PriceChooserProps {
  discount?: Partial<Discount> | null;
  intervals: number[];
  onIntervalChange: (interval: number) => void;
  onSubscriptionOptionChange: (optIn: Subscription_Options) => void;
  price: string;
  selectedInterval: number;
  subscriptionOption: Subscription_Options;
  subscriptionPrice: string | null;
}

interface PriceOption {
  label: string;
  value: number;
}

export const PriceChooser: FC<PriceChooserProps> = ({
  discount,
  intervals,
  onIntervalChange,
  onSubscriptionOptionChange,
  price,
  selectedInterval,
  subscriptionOption,
  subscriptionPrice,
}) => {
  const { currentUserId } = useContext(UserContext);
  const { amount, isPercent } = discount || {};
  const discountLabel =
    (amount &&
      (isPercent
        ? `& save ${amount}%`
        : `& save ${convertFromCentsToDollars(amount)}`)) ||
    "";
  const mapPriceOption = (days: number): PriceOption => ({
    label: `Deliver every ${days} days`,
    value: days,
  });

  const handleClick = (option: Subscription_Options) => () => {
    onSubscriptionOptionChange(option);
    logEvent(
      currentUserId,
      option === ONE_TIME
        ? EVENT_NAMES.productOneTimePurchaseSelected
        : EVENT_NAMES.productSubAndSaveSelected
    );
  };

  const isOneTime = subscriptionOption === ONE_TIME;
  const isSubSave = subscriptionOption === SUB_SAVE;

  const options: PriceOption[] = intervals.map(mapPriceOption);

  return (
    <div className={st.wrapper}>
      <div className={st.optionWrapper}>
        <div
          className={cx(
            st.oneTimePurchaseContainer,
            isOneTime && st.activeSelection
          )}
          onClick={handleClick(ONE_TIME)}
        >
          <input
            checked={isOneTime}
            name="price-option"
            onChange={() => {}}
            type="radio"
          />
          <div>
            <div className={cx(st.optionTitle, isOneTime && st.active)}>
              One-time purchase
            </div>
          </div>
        </div>
        <div
          className={cx(
            st.subscribeAndSaveContainer,
            isSubSave && st.activeSelection
          )}
          onClick={handleClick(SUB_SAVE)}
        >
          <div className={st.subscribeAndSave}>
            <input
              checked={isSubSave}
              name="price-option"
              onChange={() => {}}
              type="radio"
            />
            <div>
              <div className={cx(st.optionTitle, isSubSave && st.active)}>
                {`Subscribe ${discountLabel}`} ({subscriptionPrice || price})
              </div>
            </div>
          </div>
        </div>
      </div>
      {isSubSave && (
        <div className={st.intervalContainer}>
          {options.map((intervalOption) => {
            const checked = selectedInterval == intervalOption.value;
            return (
              <label
                className={`${checked ? st.activeCheckbox : ""}`}
                key={intervalOption.value}
              >
                <Checkbox
                  type="radio"
                  checked={checked}
                  onChange={() => {
                    intervalOption && onIntervalChange(+intervalOption.value);
                  }}
                />
                <span>{intervalOption.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};
